import { Injectable, NgZone } from '@angular/core';
import {
  StateToken,
  State,
  StateContext,
  Selector,
  Action,
  createSelector,
  Store,
} from '@ngxs/store';
import { patch, updateItem, insertItem, removeItem } from '@ngxs/store/operators';
import { tap } from 'rxjs';
import { MainTableFilterState } from 'src/app/core/state/main-table-filter.state';
import { TenantUserAccessState } from 'src/app/tenant-selector/state/tenant-user-access.state';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { UserRunTimeRoutes } from 'src/app/core/enums/routes.enum';
import { ResetCurrentFilter } from 'src/app/core/state/main-table-filter.actions';
import { IdName } from '../../shared/models/id-name';
import { ApiResponse } from '../../core/models/api-response';
import { IRunTimeImportResult } from '../models/run-time-import.model';
import {
  IRunTime,
  IRunTimeMinimal,
  IRunTimeRecordLog,
  IRunTimeRecordLogs,
} from '../models/run-time.model';
import { RunTimeApiService } from '../services/run-time-api.service';
import {
  LoadRunTimesForDepartment,
  LoadRunTimeById,
  CreateRunTime,
  UpdateRunTime,
  DeleteRunTime,
  UpdateRunTimeCounter,
  LoadAllRunTimesForDepartment,
  LoadRunTimeLog,
  UpdateOffset,
  PrintRunTimeReport,
  PrintRunTimeLog,
  LoadRunTimesForDepartmentMinimal,
  LoadRunTimeChildrenForTable,
  CreateRunTimeChild,
  ClearRunTimeChildrenForTable,
  ChangeRunTimeParent,
  ClearCurrentRunTime,
  ImportRunTimes,
  ClearRunTimeLog,
} from './run-time.actions';

export interface RunTimeStateModel {
  runTimesLoaded: boolean;
  runTimes: IRunTime[];
  orderBy: string | undefined;
  ascDesc: string | undefined;
  runTimeChildrenForTable: IRunTime[];
  runTimesMinimal: IRunTimeMinimal[];
  runTimeLogs: IRunTimeRecordLog[];
  allRunTimes: IdName[];
  newRunTimeId: string;
  departmenRunTimeMap: { [key: string]: IRunTime[] };
  departmentRunTimeTotalPages: number;
  runTimeReport: Blob;
  runTimeLog: Blob;
  currentRunTime: IRunTime | undefined;
  runTimeImportResult: IRunTimeImportResult | undefined;
}

const MAINTENANCE_JOB_STATE_TOKEN = new StateToken<RunTimeStateModel>('runTime');

@State<RunTimeStateModel>({
  name: MAINTENANCE_JOB_STATE_TOKEN,
  defaults: {
    runTimesLoaded: false,
    runTimes: [],
    orderBy: undefined,
    ascDesc: undefined,
    runTimeChildrenForTable: [],
    runTimesMinimal: [],
    runTimeLogs: [],
    allRunTimes: [],
    newRunTimeId: '',
    departmenRunTimeMap: {},
    departmentRunTimeTotalPages: 0,
    runTimeReport: <Blob>{},
    runTimeLog: <Blob>{},
    currentRunTime: undefined,
    runTimeImportResult: undefined,
  },
})
@Injectable()
export class RunTimeState {
  constructor(
    private service: RunTimeApiService,
    private zone: NgZone,
    private store: Store
  ) {}

  @Action(LoadRunTimesForDepartment)
  loadRunTimesForDepartment(
    { getState, setState }: StateContext<RunTimeStateModel>,
    { filter }: LoadRunTimesForDepartment
  ) {
    return this.service.loadRunTimesForDepartment(filter).pipe(
      tap((response: ApiResponse<IRunTime[]>) => {
        const currentDepartmentId = this.store.selectSnapshot(
          TenantUserAccessState.currentDepartmentId
        );
        const state = getState();
        const { runTimes } = state;
        const existingIds = runTimes.map((runTime) => runTime.id);
        const runTimeToAdd = response.data.filter((rt) => !existingIds.includes(rt.id));
        setState({
          ...state,
          runTimesLoaded: true,
          runTimes: [...runTimes, ...runTimeToAdd],
          departmenRunTimeMap: {
            ...state.departmenRunTimeMap,
            [currentDepartmentId]: response.data,
          },
          departmentRunTimeTotalPages: response.totalPages,
          orderBy: filter?.orderBy,
          ascDesc: filter?.ascDesc,
        });
      })
    );
  }

  @Action(LoadRunTimeChildrenForTable)
  loadRunTimeChildrenForTable(
    ctx: StateContext<RunTimeStateModel>,
    { id, filter }: LoadRunTimeChildrenForTable
  ) {
    return this.service
      .loadRunTimeChildrenForTable(id, filter, ctx.getState().orderBy, ctx.getState().ascDesc)
      .pipe(
        tap((response: ApiResponse<IRunTime[]>) => {
          ctx.setState(patch({ runTimeChildrenForTable: response.data }));
        })
      );
  }

  @Action(ClearRunTimeChildrenForTable)
  clearRunTimeChildrenForTable(ctx: StateContext<RunTimeStateModel>) {
    ctx.setState(patch({ runTimeChildrenForTable: [] }));
  }

  @Action(LoadRunTimesForDepartmentMinimal, { cancelUncompleted: true })
  loadRunTimesForDepartmentMinimal(
    ctx: StateContext<RunTimeStateModel>,
    { filter }: LoadRunTimesForDepartmentMinimal
  ) {
    return this.service.loadRunTimesForDepartmentMinimal(filter).pipe(
      tap((response: ApiResponse<IRunTimeMinimal[]>) => {
        ctx.setState(patch({ runTimesMinimal: response.data }));
      })
    );
  }

  @Action(LoadAllRunTimesForDepartment, { cancelUncompleted: true })
  loadAllRunTimesForDepartment(
    ctx: StateContext<RunTimeStateModel>,
    { includeChildren }: LoadAllRunTimesForDepartment
  ) {
    return this.service.loadAllRunTimesForDepartment(includeChildren).pipe(
      tap((allRunTimes: IdName[]) => {
        ctx.setState(patch({ allRunTimes }));
      })
    );
  }

  @Action(LoadRunTimeById, { cancelUncompleted: true })
  loadRunTimeById(
    { getState, setState }: StateContext<RunTimeStateModel>,
    { id }: LoadRunTimeById
  ) {
    const { runTimes } = getState();
    const exist = runTimes.some((i) => i.id === id);
    return this.service.loadRunTimeById(id).pipe(
      tap((runTime: IRunTime) => {
        setState(
          patch({
            runTimes: exist
              ? updateItem<IRunTime>((toUpdate) => toUpdate?.id === id, runTime)
              : insertItem<IRunTime>(runTime),
            currentRunTime: runTime,
          })
        );
      })
    );
  }

  @Action(CreateRunTime)
  @OnSuccess({ url: UserRunTimeRoutes.Main, message: 'runTimeAdded' })
  createRunTime(ctx: StateContext<RunTimeStateModel>, { runTime }: CreateRunTime) {
    return this.service.createRunTime(runTime).pipe(
      tap((result: IRunTime) => {
        ctx.setState(patch({ newRunTimeId: result.id }));
      })
    );
  }

  @Action(CreateRunTimeChild)
  @OnSuccess({ message: 'runTimeAdded' })
  createRunTimeChild(ctx: StateContext<RunTimeStateModel>, { runTime }: CreateRunTimeChild) {
    return this.service.createRunTime(runTime).pipe(
      tap((result: IRunTime) => {
        ctx.setState(patch({ newRunTimeId: result.id }));
      })
    );
  }

  @Action(UpdateRunTime)
  @OnSuccess({ url: UserRunTimeRoutes.Main, message: 'runTimeUpdated' })
  updateRunTime({ setState }: StateContext<RunTimeStateModel>, { runTime }: UpdateRunTime) {
    return this.service.updateRunTime(runTime);
  }

  @Action(ChangeRunTimeParent)
  @OnSuccess({ message: 'runTimeUpdated' })
  changeRunTimeParent(ctx: StateContext<any>, action: ChangeRunTimeParent) {
    const { id, parentRuntimeId } = action;

    return this.service.changeRunTimeParent(id, parentRuntimeId).pipe(
      tap(() => {
        this.zone.run(() => {
          ctx.dispatch(new LoadRunTimeById(id));
        });
      })
    );
  }

  @Action(UpdateRunTimeCounter)
  @OnSuccess({ message: 'runTimeCounterUpdated' })
  updateRunTimeCounter(
    { getState, dispatch }: StateContext<RunTimeStateModel>,
    { runTime }: UpdateRunTimeCounter
  ) {
    const { departmenRunTimeMap } = getState();
    const departmentId = Object.keys(departmenRunTimeMap).find((dId) =>
      departmenRunTimeMap[dId].some(({ id }) => id === runTime.id)
    );
    return this.service.updateRunTimeCounter(runTime).pipe(
      tap(() => {
        if (departmentId) {
          dispatch(new LoadRunTimesForDepartment());
        }
        this.zone.run(() => {
          this.store.dispatch(new LoadRunTimeById(runTime.id));
        });
      })
    );
  }

  @Action(UpdateOffset)
  @OnSuccess({ message: 'offsetUpdated' })
  updateOffset({ getState, dispatch }: StateContext<RunTimeStateModel>, { runTime }: UpdateOffset) {
    const { departmenRunTimeMap } = getState();
    const departmentId = Object.keys(departmenRunTimeMap).find((dId) =>
      departmenRunTimeMap[dId].some(({ id }) => id === runTime.id)
    );
    return this.service.changeOffset(runTime).pipe(
      tap(() => {
        if (departmentId) {
          dispatch(new LoadRunTimesForDepartment());
        }
        this.zone.run(() => {
          this.store.dispatch(new LoadRunTimeById(runTime.id));
        });
      })
    );
  }

  @Action(DeleteRunTime, { cancelUncompleted: true })
  @OnSuccess({
    url: UserRunTimeRoutes.Main,
    message: 'runTimeDeleted',
    dispatch: ResetCurrentFilter,
  })
  deleteRunTime(ctx: StateContext<RunTimeStateModel>, { id }: DeleteRunTime) {
    return this.service.deleteRunTime(id).pipe(
      tap(() => {
        ctx.setState(
          patch({
            runTimes: removeItem<IRunTime>(
              (jtToDelete: IRunTime | undefined) => jtToDelete?.id === id
            ),
          })
        );
      })
    );
  }

  @Action(LoadRunTimeLog, { cancelUncompleted: true })
  loadRunTimeLog(ctx: StateContext<RunTimeStateModel>, { id }: LoadRunTimeLog) {
    return this.service.loadLog(id).pipe(
      tap((logData: IRunTimeRecordLogs) => {
        ctx.setState(patch({ runTimeLogs: logData.records }));
      })
    );
  }

  @Action(ClearRunTimeLog)
  clearRunTimeLog(ctx: StateContext<RunTimeStateModel>) {
    ctx.setState(patch({ runTimeLogs: [] }));
  }

  @Action(PrintRunTimeReport, { cancelUncompleted: true })
  printRunTimeReport(
    { getState, setState }: StateContext<RunTimeStateModel>,
    { filter, menuId, includeChildren }: PrintRunTimeReport
  ) {
    return this.service.printReport(filter, menuId, includeChildren).pipe(
      tap((file: Blob) => {
        const state = getState();
        setState({
          ...state,
          runTimeReport: file,
        });
      })
    );
  }

  @Action(ImportRunTimes)
  @OnSuccess({ message: 'runTimeImportCompleted' })
  importRunTimes(ctx: StateContext<RunTimeStateModel>, { runTimeImport }: ImportRunTimes) {
    return this.service.importRunTimes(runTimeImport).pipe(
      tap((runTimeImportResult: IRunTimeImportResult) => {
        const currentFilter = this.store.selectSnapshot(
          MainTableFilterState.getCurrentFilterDetails
        );

        ctx.setState(
          patch({
            runTimeImportResult,
          })
        );

        if (currentFilter) {
          ctx.dispatch(new LoadRunTimesForDepartment(currentFilter));
        }
      })
    );
  }

  @Action(PrintRunTimeLog, { cancelUncompleted: true })
  printRunTimeLog(
    { getState, setState }: StateContext<RunTimeStateModel>,
    { id }: PrintRunTimeLog
  ) {
    return this.service.printLog(id).pipe(
      tap((file: Blob) => {
        const state = getState();
        setState({
          ...state,
          runTimeLog: file,
        });
      })
    );
  }

  @Action(ClearCurrentRunTime)
  clearCurrentRunTime(ctx: StateContext<RunTimeStateModel>) {
    ctx.setState(patch({ currentRunTime: undefined }));
  }

  @Selector()
  static getLogData(state: RunTimeStateModel): IRunTimeRecordLog[] {
    return state.runTimeLogs;
  }

  @Selector()
  static getAllRunTimes(state: RunTimeStateModel): IdName[] {
    return state.allRunTimes;
  }

  @Selector()
  static getRunTimeChildrenForTable(state: RunTimeStateModel): IRunTime[] {
    return state.runTimeChildrenForTable;
  }

  @Selector()
  static getRunTimeById(state: RunTimeStateModel) {
    return (id: string) => {
      return state.runTimes.find((runTime: IRunTime) => runTime.id === id);
    };
  }

  static getRunTimesForDepartment(departmentId: string) {
    return createSelector([RunTimeState], (state: RunTimeStateModel) => {
      return state.departmenRunTimeMap[departmentId];
    });
  }

  @Selector()
  static getRunTimesForDepartmentMinimal(state: RunTimeStateModel): IRunTimeMinimal[] {
    return state.runTimesMinimal;
  }

  @Selector()
  static getDepartmentRunTimeTotalPages(state: RunTimeStateModel): number {
    return state.departmentRunTimeTotalPages;
  }

  @Selector()
  static getRunTimeLog(state: RunTimeStateModel) {
    return state.runTimeLog;
  }

  @Selector()
  static getNewRunTimeId(state: RunTimeStateModel): string {
    return state.newRunTimeId;
  }

  @Selector()
  static getRunTimeReport(state: RunTimeStateModel) {
    return state.runTimeReport;
  }

  @Selector()
  static getCurrentRunTime(state: RunTimeStateModel): IRunTime | undefined {
    return state.currentRunTime;
  }

  @Selector()
  static getRunTimeImportResult(state: RunTimeStateModel): IRunTimeImportResult | undefined {
    return state.runTimeImportResult;
  }
}
